import React, { useState, useEffect } from 'react';
import { getReunionTypeShort } from '../utils/reunionTypes';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganisme } from '../context/OrganismeContext';
import { 
  BuildingLibraryIcon, 
  CheckCircleIcon,
  ArrowPathIcon 
} from '@heroicons/react/24/outline';

export default function TraiterPeriodesExpirees() {
  const { organismeActif } = useOrganisme();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [periodes, setPeriodes] = useState([]);
  const [membres, setMembres] = useState([]);
  const [reunions, setReunions] = useState([]);
  const [retraits, setRetraits] = useState({});
  const [renouvellements, setRenouvellements] = useState([]);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organismeActif]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Récupérer les périodes expirées
      const periodesRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/periodes/check`,
        { headers }
      );

      if (!periodesRes.data.hasExpirees) {
        // Pas de périodes expirées, rediriger
        navigate(`/admin/composition-${organismeActif.toLowerCase()}/modifier`);
        return;
      }

      setPeriodes(periodesRes.data.periodes);

      // Récupérer les membres expirés
      const fractions = periodesRes.data.periodes.map(p => p.fraction);
      const membresRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/periodes/membres-expiries`,
        { headers, params: { fractions } }
      );

      setMembres(membresRes.data);

      // Récupérer les réunions disponibles
      const reunionsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/reunions`,
        { headers }
      );

      setReunions(reunionsRes.data);

      // Initialiser les retraits avec "fin_mandat" par défaut
      const initialRetraits = {};
      membresRes.data.forEach(m => {
        initialRetraits[m._id] = {
          raisonRetrait: 'fin_mandat',
          dateRetrait: new Date().toISOString().split('T')[0],
          reunionElectionId: '',
          raisonRetraitAutre: ''
        };
      });
      setRetraits(initialRetraits);

    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRenouvellement = (membreId) => {
    if (renouvellements.includes(membreId)) {
      setRenouvellements(prev => prev.filter(id => id !== membreId));
    } else {
      setRenouvellements(prev => [...prev, membreId]);
    }
  };

  const handleRetraitChange = (membreId, field, value) => {
    setRetraits(prev => ({
      ...prev,
      [membreId]: {
        ...prev[membreId],
        [field]: value
      }
    }));
  };

  const handleValider = async () => {
    try {
      // Vérifier que tous les membres ont une réunion d'élection
      const membresNonRenouveles = membres.filter(m => !renouvellements.includes(m._id));
      const retraitsComplets = membresNonRenouveles.every(m => 
        retraits[m._id]?.reunionElectionId
      );

      const renouvellementComplets = renouvellements.every(id =>
        retraits[id]?.reunionElectionId
      );

      if (!retraitsComplets || !renouvellementComplets) {
        alert('Veuillez sélectionner une réunion d\'élection pour tous les membres');
        return;
      }

      setSaving(true);
      const token = localStorage.getItem('token');

      await axios.post(
        `${process.env.REACT_APP_API_URL}/conseil/${organismeActif}/periodes/traiter-expirees`,
        {
          retraits: membresNonRenouveles.map(m => ({
            membreId: m._id,
            ...retraits[m._id]
          })),
          renouvellements: renouvellements.map(id => ({
            membreId: id,
            reunionElectionId: retraits[id]?.reunionElectionId
          }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Périodes traitées avec succès !');
      
      // Rediriger vers la page de modification
      navigate(`/admin/composition-${organismeActif.toLowerCase()}/modifier`);

    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      alert('Erreur lors du traitement des périodes');
    } finally {
      setSaving(false);
    }
  };

  const getReunionLabel = (reunionId) => {
    const reunion = reunions.find(r => r._id === reunionId);
    if (!reunion) return '';
    return `${getReunionTypeShort(reunion.type, organismeActif)} ${new Date(reunion.date).toLocaleDateString('fr-FR')}`;
  };

  const getFractionLabel = (fraction) => {
    return fraction.replace('_', ' ').replace('er', 'er').replace('e', 'e');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Grouper les membres par fraction
  const membresByFraction = {};
  periodes.forEach(p => {
    membresByFraction[p.fraction] = membres.filter(m => m.fraction === p.fraction);
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <BuildingLibraryIcon className="h-8 w-8 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Traitement des périodes expirées
              </h1>
              <p className="text-sm text-gray-500">
                {organismeActif} - Incrémentation des périodes
              </p>
            </div>
          </div>

          <div className="bg-honey-50 border border-honey-200 rounded-lg p-4">
            <p className="text-sm text-honey-900">
              <strong>Important :</strong> Vous devez traiter tous les membres des fractions expirées avant de pouvoir continuer. 
              Pour chaque membre, vous pouvez soit le retirer définitivement, soit le renouveler pour la période suivante.
            </p>
          </div>
        </div>

        {/* Liste des fractions */}
        {periodes.map(periode => (
          <div key={periode._id} className="bg-white rounded-lg shadow-sm mb-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {getFractionLabel(periode.fraction)}
                </h2>
                <span className="px-3 py-1 bg-honey-100 text-honey-800 rounded-full text-sm font-medium">
                  Période {periode.periodeDebut}-{periode.periodeFin}
                </span>
              </div>
            </div>

            <div className="p-6">
              {membresByFraction[periode.fraction]?.length === 0 ? (
                <p className="text-gray-500 text-sm italic">Aucun membre actif dans cette fraction</p>
              ) : (
                <div className="space-y-4">
                  {membresByFraction[periode.fraction]?.map(membre => {
                    const isRenouvele = renouvellements.includes(membre._id);
                    
                    return (
                      <div key={membre._id} className="border border-gray-200 rounded-lg p-4">
                        {/* Identité du membre */}
                        <div className="mb-4">
                          <h3 className="font-medium text-gray-900">
                            {membre.adherent?.prenom} {membre.adherent?.nom}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Position {membre.ordre} - {getFractionLabel(membre.fraction)}
                          </p>
                        </div>

                        {/* Renouvellement : au-dessus de réunion / retrait */}
                        <div className="mb-4 pb-4 border-b border-gray-100">
                          <label
                            htmlFor={`renouveler-${membre._id}`}
                            className="flex items-start gap-3 cursor-pointer rounded-lg p-2 -m-2 hover:bg-indigo-50/60 transition-colors"
                          >
                            <input
                              id={`renouveler-${membre._id}`}
                              type="checkbox"
                              checked={isRenouvele}
                              onChange={() => handleToggleRenouvellement(membre._id)}
                              className="h-5 w-5 mt-0.5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                            />
                            <span>
                              <span className="block text-sm font-medium text-gray-900">
                                Renouveler le mandat pour la période suivante
                              </span>
                              <span className="block text-xs text-gray-500 mt-1">
                                Si la case est cochée, le membre est maintenu pour la nouvelle période (pas de retrait). Sinon, saisissez le retrait ci-dessous.
                              </span>
                            </span>
                          </label>
                        </div>

                        {/* Formulaire : réunion puis raison (retrait seulement) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label
                              htmlFor={`reunion-${membre._id}`}
                              className="block text-sm font-medium text-gray-700 mb-1"
                            >
                              {isRenouvele
                                ? 'Réunion de renouvellement (date de l’acte) *'
                                : 'Réunion de retrait (date de l’acte) *'}
                            </label>
                            <select
                              id={`reunion-${membre._id}`}
                              value={retraits[membre._id]?.reunionElectionId || ''}
                              onChange={(e) => handleRetraitChange(membre._id, 'reunionElectionId', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              required
                            >
                              <option value="">Sélectionner une réunion</option>
                              {reunions.map(r => (
                                <option key={r._id} value={r._id}>
                                  {getReunionLabel(r._id)}
                                </option>
                              ))}
                            </select>
                          </div>

                          {!isRenouvele && (
                            <>
                              <div>
                                <label
                                  htmlFor={`raison-${membre._id}`}
                                  className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                  Raison du retrait
                                </label>
                                <select
                                  id={`raison-${membre._id}`}
                                  value={retraits[membre._id]?.raisonRetrait || 'fin_mandat'}
                                  onChange={(e) => handleRetraitChange(membre._id, 'raisonRetrait', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                >
                                  <option value="fin_mandat">Fin de mandat</option>
                                  <option value="demission">Démission</option>
                                  <option value="non_adhesion">Non-adhésion</option>
                                  <option value="autre">Autre</option>
                                </select>
                              </div>

                              {retraits[membre._id]?.raisonRetrait === 'autre' && (
                                <div className="md:col-span-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Précisez la raison
                                  </label>
                                  <input
                                    type="text"
                                    value={retraits[membre._id]?.raisonRetraitAutre || ''}
                                    onChange={(e) => handleRetraitChange(membre._id, 'raisonRetraitAutre', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Précisez..."
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {isRenouvele && (
                          <div className="mt-3">
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                              <CheckCircleIcon className="h-4 w-4" />
                              Mandat renouvelé (pas de retrait)
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <p className="font-medium">Récapitulatif :</p>
              <p className="mt-1">
                {membres.length - renouvellements.length} retrait(s) • {renouvellements.length} renouvellement(s)
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => navigate('/admin/composition-conseil')}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleValider}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    Traitement en cours...
                  </>
                ) : (
                  'Valider et incrémenter les périodes'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
